import {
	CreateDrawerFormProps,
	FormDrawerContentProps,
	I18nFormDrawer,
	I18nFormDrawerContent,
	I18nFormFetchDrawer,
	I18nOnlyOnMainForm,
	I18nTextField,
	UpdateDrawerFormProps,
	useDynamicConfig,
	validateEachLanguage,
} from "@dgs/core";
import { FormikErrors } from "formik";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { industryService } from "../shared/api/industryService";
import { IndustryFormValues } from "../shared/types";
import { UserMultiSelectField } from "../users/UserSelectField";

const useValidate = () => {
	const { t } = useTranslation();

	return (values: IndustryFormValues) => {
		const errors: FormikErrors<IndustryFormValues> = {};
		return {
			...errors,
			...validateEachLanguage<IndustryFormValues["translations"][number]>(values.translations, (_, translation) => {
				const errors: Record<string, any> = {};
				if (!translation.name) {
					errors.name = t("Field is required");
				}
				return errors;
			}),
		};
	};
};

const emptyTranslation = { name: "" };

const IndustryFormDrawerContent: FC<FormDrawerContentProps> = (props) => {
	const { t } = useTranslation();

	return (
		<I18nFormDrawerContent {...props} emptyTranslation={emptyTranslation}>
			<I18nTextField name="name" label={t("Name")} required="onlyOnMainForm" />
			<I18nOnlyOnMainForm>
				<UserMultiSelectField
					name="users"
					label={t("Approving users")}
					description={t("The users selected here can be chosen as approvers in the Event Hub.")}
				/>
			</I18nOnlyOnMainForm>
		</I18nFormDrawerContent>
	);
};

export const IndustryCreateDrawer: FC<CreateDrawerFormProps<IndustryFormValues>> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();
	const { languageSettings } = useDynamicConfig();

	const initialValues: IndustryFormValues = useMemo(
		() => ({
			translations: [{ locale: languageSettings.fallback, name: "" }],
			users: [],
		}),
		[languageSettings.fallback],
	);

	return (
		<I18nFormDrawer
			{...props}
			initialValues={initialValues}
			title={t("Create industry")}
			validate={validate}
			renderForm={(formProps) => <IndustryFormDrawerContent {...formProps} {...props} />}
		/>
	);
};

export const IndustryUpdateDrawer: FC<UpdateDrawerFormProps<IndustryFormValues>> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();

	return (
		<I18nFormFetchDrawer
			{...props}
			title={t("Edit industry")}
			queryFn={() => industryService.show(props.id).then((x) => x.data.data)}
			queryKey={industryService.showKey(props.id)}
			validate={validate}
			renderSuccess={(formProps) => <IndustryFormDrawerContent {...props} {...formProps} />}
		/>
	);
};
